/**
 * Phase 3c §5 (A1) — Team access card on the employer portal dashboard.
 *
 * Minimal inline UI listing `primary_contact` + `employer_admins[]`, with an
 * inline add-admin form and per-row Remove buttons. Self-removal is blocked
 * at the API; the UI greys the Remove action and tooltips why.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    addEmployerAdmin,
    formatApiError,
    listEmployerAdmins,
    removeEmployerAdmin,
} from "../../lib/api";
import { track } from "../../lib/analytics";
import "./TeamAccessCard.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TeamAccessCard({ employerId, employerName }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addEmail, setAddEmail] = useState("");
    const [addRole, setAddRole] = useState("");
    const [adding, setAdding] = useState(false);
    const [sentMsg, setSentMsg] = useState("");
    const [err, setErr] = useState("");
    const [pendingRemove, setPendingRemove] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await listEmployerAdmins(employerId);
            setRows(res.admins || []);
        } catch (_) { /* ignore */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); track("team_access_card_view", { employer_id: employerId }); /* eslint-disable-line */ }, []);

    const submitAdd = async (e) => {
        e?.preventDefault?.();
        setErr("");
        setSentMsg("");
        const v = addEmail.trim();
        if (!EMAIL_RE.test(v)) {
            setErr(t("auth.teamAccess.add.err"));
            return;
        }
        setAdding(true);
        try {
            const res = await addEmployerAdmin(employerId, { email: v, role: addRole.trim() || null });
            track("team_access_admin_added", { idempotent: !!res.idempotent });
            if (res.idempotent) {
                setSentMsg(t("auth.teamAccess.add.alreadyIn"));
            } else {
                setSentMsg(t("auth.teamAccess.add.sent", { email: v }));
            }
            setAddEmail("");
            setAddRole("");
            await load();
        } catch (e2) {
            setErr(formatApiError(e2) || t("auth.teamAccess.add.err"));
        } finally {
            setAdding(false);
        }
    };

    const confirmRemove = async (email) => {
        if (!window.confirm(t("auth.teamAccess.confirmRemove", { email }))) return;
        setPendingRemove(email);
        setErr("");
        try {
            await removeEmployerAdmin(employerId, email);
            track("team_access_admin_removed", {});
            await load();
        } catch (e2) {
            setErr(formatApiError(e2) || t("auth.teamAccess.add.err"));
        } finally {
            setPendingRemove(null);
        }
    };

    return (
        <section className="tac" data-testid="team-access-card">
            <div className="tac-head">
                <div className="mono tac-label">{t("auth.teamAccess.label")}</div>
                <h2 className="tac-title">{t("auth.teamAccess.title")}</h2>
                <p className="tac-sub">{t("auth.teamAccess.sub", { employer: employerName })}</p>
            </div>

            {loading ? (
                <div className="mono tac-loading">Loading…</div>
            ) : (
                <ul className="tac-list" data-testid="team-access-list">
                    {rows.map((r) => (
                        <li
                            key={r.email}
                            className={`tac-row ${r.is_primary ? "is-primary" : ""}`}
                            data-testid={`team-access-row-${r.email}`}
                        >
                            <div className="tac-row-main">
                                <div className="tac-row-email">{r.email}</div>
                                <div className="tac-row-meta mono">
                                    {r.is_primary && (
                                        <span className="tac-badge">{t("auth.teamAccess.primaryRoleBadge")}</span>
                                    )}
                                    {r.role && !r.is_primary && <span>{r.role}</span>}
                                    {r.granted_at && (
                                        <span className="tac-row-meta-sep">
                                            · {t("auth.teamAccess.grantedLabel")} {new Date(r.granted_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="tac-remove"
                                disabled={r.is_self || r.is_primary || pendingRemove === r.email}
                                title={
                                    r.is_self
                                        ? t("auth.teamAccess.removeSelfHint")
                                        : r.is_primary
                                            ? t("auth.teamAccess.removePrimaryHint")
                                            : undefined
                                }
                                onClick={() => confirmRemove(r.email)}
                                data-testid={`team-access-remove-${r.email}`}
                            >
                                {pendingRemove === r.email
                                    ? t("auth.teamAccess.removing")
                                    : t("auth.teamAccess.remove")}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <form className="tac-add" onSubmit={submitAdd}>
                <h3 className="tac-add-head mono">{t("auth.teamAccess.add.heading")}</h3>
                <div className="tac-add-grid">
                    <label className="apply-field">
                        <span className="mono">{t("auth.teamAccess.add.emailLabel")}</span>
                        <input
                            type="email"
                            value={addEmail}
                            onChange={(e) => setAddEmail(e.target.value)}
                            placeholder={t("auth.teamAccess.add.emailPlaceholder")}
                            data-testid="team-access-add-email"
                        />
                    </label>
                    <label className="apply-field">
                        <span className="mono">{t("auth.teamAccess.add.roleLabel")}</span>
                        <input
                            type="text"
                            value={addRole}
                            onChange={(e) => setAddRole(e.target.value)}
                            placeholder={t("auth.teamAccess.add.rolePlaceholder")}
                            data-testid="team-access-add-role"
                        />
                    </label>
                </div>
                {sentMsg && <div className="tac-ok" data-testid="team-access-add-ok">{sentMsg}</div>}
                {err && <div className="apply-err" data-testid="team-access-add-err">{err}</div>}
                <button
                    type="submit"
                    className="btn-apply-submit tac-add-btn"
                    disabled={adding || !addEmail}
                    data-testid="team-access-add-submit"
                >
                    {adding ? t("auth.teamAccess.add.submitting") : t("auth.teamAccess.add.submit")}
                </button>
            </form>
        </section>
    );
}
